import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { CButton } from "../../../../common/buttons";
import CustomInput from "../../../../common/form/CustomInput";
import { InputWarning } from "../../../../common/form/warnings";
import styles from "./add_master.module.scss";
import { Checkbox, IconButton } from "@material-ui/core";
import { AutoSuggest } from "../../../../common/form/AutoSuggest";
import CloseIcon from "@material-ui/icons/Close";
import { getTeamMasterSpecializations } from "../../services";
import { getPhoneNumbers, PhonesInput } from "./PhonesInput";
import { CardInput } from "../../../../common/form/InputNumberMask";
import { masterTypes } from "../../../../../constants";

export const AddExternalTeamMaster = ({ data, handleClose }) => {
  const {
    handleSubmit,
    control,
    formState: { errors }
  } = useForm();
  const { modalLoading } = useSelector(state => state.appState);
  const [specializations, setSpecializations] = useState([]);
  const [phonesInfo, setPhonesInfo] = useState({
    phones: data?.body?.phone_numbers || [],
    phoneNumber: "",
    activePhoneId: ""
  });
  useEffect(() => {
    const getData = async () => {
      const res = await getTeamMasterSpecializations({
        team_master_id: data?.body?.team_master_id,
        type_code: data.type
      });
      if (!res) return;
      setSpecializations(res);
    };
    getData();
  }, [data]);

  const onSubmit = async body => {
    const { onSubmit: submit } = data;
    return (
      submit &&
      (await submit(
        {
          ...body,
          team_master_id: data?.body?.team_master_id,
          phone_numbers: getPhoneNumbers(phonesInfo),
          team_master_doc_num: body?.team_master_doc_num?.toUpperCase() || ""
        },
        data?.body?.team_id
      ))
    );
  };

  const handlePhoneChange = useCallback(
    b => setPhonesInfo(prev => ({ ...prev, ...b })),
    []
  );

  const specialityDefaultValue = useMemo(() => {
    if (!data?.body?.specialities?.length) return [];
    return data?.body?.specialities.map(item => item.speciality_id);
  }, [data.body]);

  return (
    <div>
      <div className={styles.add_team__header}>
        <h2 className={styles.add_team__title}>{` ${
          data?.body ? "Редактирование" : "Добавление"
        } мастера`}</h2>
        <IconButton
          size="small"
          aria-label="close"
          color="inherit"
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
      </div>

      <form className={styles.add_team__form} onSubmit={handleSubmit(onSubmit)}>
        <div>
          <span className={styles.add_team__label}>Личные данные</span>
          <div className={styles.add_team__form_row}>
            <div className={styles.add_team__form_item}>
              <Controller
                name="team_master_fio"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CustomInput
                    type="text"
                    fullWidth
                    label="ФИО"
                    placeholder="Введите ФИО"
                    wrapperClassname={styles.add_team__input_wrapper}
                    value={value}
                    onChange={onChange}
                  />
                )}
                defaultValue={data?.body?.team_master_fio || ""}
                rules={{
                  required: true
                }}
              />
              {errors.team_master_fio && (
                <InputWarning>Это поле обязательно</InputWarning>
              )}
            </div>
            <div className={styles.add_team__form_item}>
              <Controller
                name="team_master_doc_num"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CustomInput
                    uppercase
                    type="text"
                    fullWidth
                    label="Номер документа"
                    placeholder="Введите номер документа"
                    wrapperClassname={styles.add_team__input_wrapper}
                    value={value}
                    onChange={onChange}
                  />
                )}
                defaultValue={data?.body?.team_master_doc_num || ""}
                rules={{
                  required: true
                }}
              />
              {errors.team_master_doc_num && (
                <InputWarning>Это поле обязательно</InputWarning>
              )}
            </div>
          </div>
          <div className={styles.add_team__form_row}>
            <div className={styles.add_team__form_item}>
              <PhonesInput
                phone_numbers={phonesInfo.phones}
                handleChange={handlePhoneChange}
              />
            </div>
            {data.type === masterTypes.external && (
              <div className={styles.add_team__form_item}>
                <Controller
                  name="card_num"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <CardInput
                      fullWidth
                      label="Номер карты"
                      placeholder="Введите номер карты"
                      wrapperClassname={styles.add_team__input_wrapper}
                      className={styles.add_team__input_phone}
                      value={value}
                      onChange={onChange}
                    />
                  )}
                  defaultValue={data?.body?.card_num || ""}
                  rules={{
                    required: false,
                    maxLength: 16,
                    minLength: 16
                  }}
                />
                {errors.card_num && (
                  <InputWarning>
                    {errors.card_num?.type === "required"
                      ? "Это поле обязательно"
                      : "Не корректный номер карты"}
                  </InputWarning>
                )}
              </div>
            )}
          </div>
          <div className={styles.add_team__form_row}>
            <div className={styles.add_team__form_item}>
              <Controller
                name="speciality_ids"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <AutoSuggest
                    list={specializations}
                    showKey={"speciality_name"}
                    withMargin
                    showMultipleSelected
                    multiple
                    valueKey={"speciality_id"}
                    value={value}
                    placeholder="Выберите специализацию"
                    label="Специализация"
                    onChange={onChange}
                  />
                )}
                defaultValue={specialityDefaultValue}
              />
            </div>
          </div>
        </div>
        <div className={styles.add_team__form_row}>
          <div className={`${styles.add_team__form_itemCheckbox}`}>
            <label htmlFor={"is_active_modal_check"}>Активный</label>
            <Controller
              name="is_active"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Checkbox
                  id={"is_active_modal_check"}
                  checked={!!value}
                  onChange={onChange}
                />
              )}
              defaultValue={
                data?.body?.is_active || data?.body?.is_active !== false
              }
            />
          </div>
        </div>
        <div className={styles.add_team__btn_wrapper}>
          <CButton
            loading={modalLoading}
            disabled={modalLoading}
            type="submit"
            className={styles.add_team__btn}
          >
            Сохранить
          </CButton>
        </div>
      </form>
    </div>
  );
};
